import React from 'react';
import { styled } from '@mui/material/styles';
import { css } from '@emotion/react';
import Image from '../Image';
import { File } from '../Media/Media.types';
import { ArtDirectedImageProps } from './ArtDirectedImage.types';

/**
 * Originally, the tablet version was shown on widths between 768.1px and 1024px.
 * However, this was changed to 768.1px and 1440px to ensure that there's
 * enough space in the top navigation bar to include all navigation items.
 * This could be changed back in the future when we redesign the navigation bar.
 *
 * AFAIK no other image is using the tablet variant.
 */
const getImageMedia = (
  breakpoint: string,
  { fileMobile, fileTablet }: { file?: File; fileTablet?: File; fileMobile?: File },
) => {
  // TODO: Export breakpoints from theme/index.ts file and use them here
  switch (breakpoint) {
    case 'mobile':
      return '(max-width: 768px)';
    case 'tablet':
      return '(min-width: 768.1px) and (max-width: 1440px)';
    case 'desktop':
      if (!fileTablet && !fileMobile) {
        return '';
      } else if (fileMobile && !fileTablet) {
        return '(min-width: 768.1px)';
      } else if (fileTablet) {
        return '(min-width: 1440.1px)';
      }
  }

  return '';
};

const ArtDirectedImage = React.forwardRef<HTMLImageElement, ArtDirectedImageProps>(
  ({ title, description, file, fileMobile, fileTablet, ...props }, ref) => (
    <>
      {fileMobile?.url ? (
        <>
          <ResponsiveImage
            src={fileMobile?.url}
            columns={6}
            height={fileMobile?.height}
            media={getImageMedia('mobile', { file, fileMobile, fileTablet })}
            alt={description || title}
            {...props}
          />
        </>
      ) : null}

      {fileTablet?.url ? (
        <>
          <ResponsiveImage
            src={fileTablet?.url}
            columns={8}
            height={fileTablet?.height}
            media={getImageMedia('tablet', { file, fileMobile, fileTablet })}
            alt={description || title}
            {...props}
          />
        </>
      ) : null}

      {file?.url ? (
        <>
          <ResponsiveImage
            ref={ref}
            src={file?.url}
            columns={12}
            height={file?.height}
            media={getImageMedia('desktop', { file, fileMobile, fileTablet })}
            alt={description || title}
            {...props}
          />
        </>
      ) : null}
    </>
  ),
);

const ResponsiveImage = styled(Image, {
  name: 'ArtDirectedImage',
  slot: 'Root',
  shouldForwardProp: (prop) => prop !== 'displaymedia',
})<{
  media?: string;
}>`
  ${({ media }) =>
    media
      ? css`
          display: none !important;

          @media ${media} {
            display: block !important;
            visibility: visible !important;
          }
        `
      : ''}
`;

export default React.memo(ArtDirectedImage);
